import { useRouter } from 'next/router';

import { Loading } from '~/_legacy/components/molecules/loading';

import { useSWRV1OrganizationByIdCompetitions } from '~/api/v1/organizations/[id]/competitions/get';
import { useSWRV1OrganizationById } from '~/api/v1/organizations/[id]/get';
import { useSWRV2CorporateByIdPostsFeatured } from '~/api/v2/corporates/[id]/posts/featured/get';
import { useSWRV2OrganizationByIdChallenges } from '~/api/v2/organizations/[id]/challenges/get';
import { useSWRV2OrganizationByIdLocations } from '~/api/v2/organizations/[id]/locations/get';
import { useSWRV2OrganizationByIdPostsFeatured } from '~/api/v2/organizations/[id]/posts/featured/get';
import { useSWRV2OrganizationByIdPosts } from '~/api/v2/organizations/[id]/posts/get';
import { useSWRV2OrganizationByIdPresentations } from '~/api/v2/organizations/[id]/presentations/get';
import { Avatar } from '~/components/Avatar/Avatar';
import { LabelArea } from '~/components/Label/LabelArea';
import { Tabs } from '~/components/Tabs/Tabs';
import { SusnetImage } from '~/components/_wrapper/SusnetImage';
import { getNameWithCategory } from '~/domains/corp-org/getNameWithCategory';
import Layout from '~/pages/_layout.page';

import { SposUidTabList } from './_tabPanel/state';
import { isNotEmpty } from '~/utils/array';

export default function Page() {
  const router = useRouter();
  const uid = router.query.uid as string;

  const { org, isLoading } = useSWRV1OrganizationById(uid);
  const { competitions, isLoading: isLoadingOrgCompetitions } =
    useSWRV1OrganizationByIdCompetitions(uid);
  const { orgLocations } = useSWRV2OrganizationByIdLocations(uid);
  const { presentations, isLoading: isLoadingOrgPresentations } =
    useSWRV2OrganizationByIdPresentations(uid);
  const { featuredPosts } = useSWRV2OrganizationByIdPostsFeatured(uid);
  const { posts, isLoading: isLoadingPosts } = useSWRV2OrganizationByIdPosts({ uid });
  const { challenges } = useSWRV2OrganizationByIdChallenges(uid);

  // isLoadingの場合はLoadingコンポーネントを表示
  if (isLoading) {
    return (
      <Layout pageTitle={'SPO詳細ページ'} pageDescription={'SPO詳細ページ'}>
        <Loading />
      </Layout>
    );
  }

  // orgが存在しない場合は404ページを表示
  if (!org) {
    return (
      <Layout pageTitle={'SPO詳細ページ'} pageDescription={'SPO詳細ページ'}>
        <div>404</div>
      </Layout>
    );
  }

  return (
    <Layout
      pageTitle={getNameWithCategory(org)}
      pageDescription={org.vision}
      pageImage={org.cover}
      pageUrl={`https://susnet.jp/orgs/${uid}`}
    >
      <header className='mx-auto flex w-full max-w-[1280px] flex-col items-center lg:h-[300px] lg:justify-end'>
        <div className='flex w-full items-center gap-s__sp px-l__sp py-s__sp lg:w-[87.5%] lg:gap-xl lg:p-0'>
          <div className='relative h-12 w-12 lg:w-[104px]'>
            <div className='absolute z-10 h-full w-full lg:h-[104px]'>
              <Avatar customSize='h-full w-full' src={org.logo ?? '/icons/corp-icon.png'} alt={org.name} />
            </div>
          </div>
          <div className='flex flex-1 flex-col justify-center lg:flex-row lg:justify-start lg:gap-m'>
            <h1 className='text-title-x-small font-bold text-primary lg:text-title-medium lg:font-bold'>
              {org.name}
            </h1>
            <LabelArea areaText={org.area} />
          </div>
        </div>
        {isNotEmpty(org.cover) && org.cover !== '' && (
          <div className='relative mx-auto h-[124px] w-full max-w-[1920px] lg:h-[216px]'>
            <SusnetImage
              src={org.cover}
              alt={`cover-${org.name}`}
              fill
              sizes='100vw'
              className='object-cover'
            />
          </div>
          )}
      </header>

      <main className='mx-auto w-full max-w-[1280px] overflow-hidden'>
        <Tabs
          tabs={SposUidTabList({
            uid,
            org,
            competitions,
            isLoadingOrgCompetitions,
            orgLocations,
            presentations,
            isLoadingOrgPresentations,
            featuredPosts,
            posts,
            isLoadingPosts,
            challenges,
          })}
        />
      </main>
    </Layout>
  );
}
